<template>
  <v-layout class="layout">
    <v-navigation-drawer v-model="drawer" :rail="rail" expand-on-hover permanent class="drawer" theme="dark"
      @update:rail="onRail">
      <v-list nav class="nav-list">
        <v-img :width="300" aspect-ratio="16/9" cover src="img/no-payoff.png" class="logo"></v-img>
        <v-btn class="rail-toggler" variant="text" :icon="rail ? 'mdi-view-list' : 'mdi-dots-vertical'"
          @click.stop="rail = !rail" />
        <v-divider :thickness="2"></v-divider>
        <v-list-item prepend-icon="mdi-view-dashboard" :title="$t('side_bar.routes_name.dashboard')"
          :to="`${PAGES.DASHBOARD.PATH}?${searchParamsString}`"></v-list-item>
        <template v-if="pipelinesStore.selectedPipeline">
          <v-list-item :disabled="!pipelinesStore.selectedInspectionStats" prepend-icon="mdi-chart-box-outline"
            :title="$t('side_bar.routes_name.anomalies')"
            :to="`${PAGES.ANOMALIES.PATH}?${searchParamsString}`"></v-list-item>
          <v-list-item :disabled="!pipelinesStore.selectedInspectionStats" prepend-icon="mdi-chart-box-outline"
            :title="$t('side_bar.routes_name.joints')" :to="`${PAGES.JOINTS.PATH}?${searchParamsString}`"></v-list-item>
          <v-list-item :disabled="!pipelinesStore.selectedInspectionStats" prepend-icon="mdi-chart-box-outline"
            :title="$t('side_bar.routes_name.pipe_parts')"
            :to="`${PAGES.PIPE_PARTS.PATH}?${searchParamsString}`"></v-list-item>
          <v-list-item :disabled="!pipelinesStore.selectedInspectionStats ||
            !pipelinesStore.selectedInspectionStats.safety_factor_available
            " prepend-icon="mdi-chart-box-outline" :title="$t('side_bar.routes_name.safety_factor')"
            :to="`${PAGES.SAFETY_FACTOR.PATH}?${searchParamsString}`"></v-list-item>
        </template>
        <v-list-item v-if="scannerControlStore.isScannerControlPageAvailable"
          prepend-icon="mdi-map-marker-multiple-outline" :title="$t('side_bar.routes_name.scanner_control')"
          :to="`${PAGES.SCANNER_CONTROL.PATH}?${searchParamsString}`"></v-list-item>
        <v-list-item prepend-icon="mdi-cog" :title="$t('side_bar.routes_name.settings')"
          :to="`${PAGES.SETTINGS.PATH}?${searchParamsString}`"></v-list-item>
        <v-list-item :active="false" @click="onClickShowScanners">
          <template #prepend>
            <div class="switch">
              <v-switch :model-value="mapStore.showMapScanners" :hide-details="true" :loading="isLoadingPipeScanners[SCANNER_TYPE.RESULTS]
                ? 'warning'
                : false
                " :disabled="isLoadingPipeScanners[SCANNER_TYPE.RESULTS]" density="compact"></v-switch>
            </div>
          </template>
          <template #title>{{ $t("side_bar.show_scanners") }} </template>
        </v-list-item>
        <v-list-item :active="false" @click="mapStore.switchMapmode">
          <template #prepend>
            <div class="switch">
              <v-switch :model-value="mapStore.isMapMode" :hide-details="true" density="compact"></v-switch>
            </div>
          </template>
          <template #title>{{ $t("side_bar.map_mode") }} </template>
        </v-list-item>
      </v-list>
      <v-text-field v-if="authStore.serviceGroups.length > 1" v-model="searchTerm" dense hide-details clearable :placeholder="$t('side_bar.search_groups')"
        class="group-search" />
      <v-list v-model:opened="initiallyOpenedLists" nav class="btns-list">
        <NavBarGroupItem v-if="filteredServiceGroups.length > 0" :groups="filteredServiceGroups" value="serviceGroups"
          :show-nested="showNested">
        </NavBarGroupItem>
        <v-divider :thickness="2"></v-divider>
        <NavBarAdminItem v-if="authStore.isAdmin" :show-nested="showNested"></NavBarAdminItem>
        <v-list-item prepend-icon="mdi-login-variant" title="Logout" @click="logout"></v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-main :class="{ expanded: rail }">
      <slot></slot>
    </v-main>
  </v-layout>
</template>

<script>
import NavBarGroupItem from "./NavBarGroupItem.vue";
import NavBarAdminItem from "./NavBarAdminItem.vue";

import { administrationAPI } from "@/features/administration/api";
import { SCANNER_TYPE } from "@/pages/scanner-control/config";
import {
  useAuthStore,
  useGlobalStore,
  useMapStore,
  usePipelinesStore,
  usePipeScannersStore,
  useScannerControlStore,
} from "@/store";
import {
  useSelectedGroupInURL,
  useSelectedPipelineIdInURL,
} from "@/utils/urlHelpers";
import { PAGES } from "@/constants/pagesNames";
import { nextTick, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useI18n } from "vue-i18n";

export default {
  components: {
    NavBarGroupItem,
    NavBarAdminItem,
  },

  setup() {
    const { pipelineIdInURL } = useSelectedPipelineIdInURL();
    const { groupInURL } = useSelectedGroupInURL();
    const pipelinesStore = usePipelinesStore();
    const authStore = useAuthStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();

    onMounted(async () => {
      watch(() => route.query, removeRedundantSearchParams, { once: true });

      await authStore.fetchPermissionsAndGroups();

      watch(
        () => [groupInURL.value, authStore.serviceGroups],
        ([groupInURL, groups]) => {
          if (groups.length) {
            const doesGroupInURLExist = groups.includes(groupInURL);
            let groupToSet = null;
            if (doesGroupInURLExist) {
              groupToSet = groupInURL;
            } else if (groups.length === 1) {
              groupToSet = groups[0];
            }
            if (!doesGroupInURLExist && groupInURL)
              toast.error(t("dashboard.groups.doesnt_exist"), {
                position: "top-right",
                duration: 4000,
              });

            if (groupToSet) authStore.setSelectedGroup(groupToSet);
          }
        },
        { immediate: true }
      );

      watch(
        () => authStore.selectedGroup,
        (group) => {
          if (group) authStore.getGroupData();
        },
        { immediate: true }
      );

      watch(
        () => [
          pipelineIdInURL.value,
          pipelinesStore.groupPipelines.length,
          authStore.selectedGroup,
        ],
        async ([id, groupPipelinesLen, group]) => {
          if (!id) {
            pipelinesStore.resetSelectedPipelineIdAndAllDataForIt();
          } else if (groupPipelinesLen && group) {
            await nextTick();
            pipelinesStore.setSelectedPipelineIdFromURL();
          }
        }
      );
    });

    function removeRedundantSearchParams() {
      const paramsToRemove = ["code", "state"];
      const newQuery = { ...route.query };
      let shouldRouterReplace = false;

      paramsToRemove.forEach((param, _, arr) => {
        if (arr.includes(param)) {
          delete newQuery[param];
          shouldRouterReplace = true;
        }
      });

      if (shouldRouterReplace) {
        router.replace({
          query: newQuery,
        });
      }
    }

    return {
      PAGES,
      SCANNER_TYPE,
      administrationAPI,
      pipelineIdInURL,
      pipelinesStore,
      authStore,
      mapStore: useMapStore(),
      pipeScannersStore: usePipeScannersStore(),
      scannerControlStore: useScannerControlStore(),
      globalStore: useGlobalStore(),
    };
  },

  data() {
    return {
      drawer: true,
      rail: false,
      showNested: false,
      requests: new Map(),
      initiallyOpenedLists: ["serviceGroups"],
      searchTerm: "",
    };
  },

  computed: {
    filteredServiceGroups() {
      if (!this.searchTerm) return this.authStore.serviceGroups;
      const term = this.searchTerm.toLowerCase();
      return this.authStore.serviceGroups.filter(group =>
        group.toLowerCase().includes(term)
      );
    },

    isLoadingPipeScanners() {
      return this.pipeScannersStore.isLoading;
    },
    searchParamsString() {
      return new URLSearchParams(this.$route.query).toString();
    },
  },

  methods: {
    logout() {
      this.$auth0.logout({
        logoutParams: { returnTo: window.location.origin },
      });
    },

    onRail(value) {
      if (this.rail && value) {
        this.showNested = false;
      } else {
        this.showNested = true;
      }
    },

    onClickShowScanners() {
      if (this.isLoadingPipeScanners[SCANNER_TYPE.RESULTS]) {
        return;
      }

      this.mapStore.switchMapScanners();

      if (
        this.mapStore.showMapScanners &&
        this.pipeScannersStore.pipeScanners[SCANNER_TYPE.RESULTS].length === 0
      ) {
        this.pipeScannersStore.fetchResultsScanners();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/scss/variables.scss" as *;

.layout {
  height: 100vh;

  .v-main {
    transition: width 0.5s;
    padding: 0 !important;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 256px;
    right: 0;

    &.expanded {
      left: 56px;
    }
  }
}

.drawer {
  background: $black-primary !important;
  position: relative;
  z-index: 10000 !important;

  .nav-list {
    padding-bottom: 0;
  }

  .btns-list {
    padding-top: 0;
  }

  .logo {
    margin: 15px 0;
  }

  div.switch {
    width: 56px;
  }

  :deep(.rail-toggler) {
    position: absolute;
    top: 15px;
    left: 200px;
  }

  :deep(.v-list-item) {
    border-radius: 10px;
    margin-top: 6px;
    cursor: pointer;

    &:hover {
      background: #3b4151;
    }
  }

  :deep(.v-list-item--active) {
    background: linear-gradient(60deg, $cyan-main, $cyan-dark);
  }

  :deep(.v-list-item-title) {
    font-size: 14px !important;
  }
}

.group-search {
margin: 10px 8px;
}

.group-search :deep(.v-field) {
border-top-left-radius: 10px;
border-top-right-radius: 10px;
overflow: hidden;
}

.group-search :deep(.v-field__input) {
padding: 0 10px;
min-height: 48px;
}

.group-search :deep(input) { font-size: 14px; }
</style>
